import { Link } from 'react-router-dom';
import MetaHead from '../../components/MetaHead';

export default function KunstterapiPage() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
      <MetaHead 
        title="Tegne- / kunstterapi - Din Privat Psykolog"
        description="Tegneterapi og kunstterapi i klinikken i Gram. Kreativt terapeutisk redskab til at tilgå komplekse følelser og tilstande ved autoriseret psykolog Marie Aagaard Larsen."
        ogTitle="Tegne- / kunstterapi - Din Privat Psykolog"
        ogDescription="Tegneterapi og kunstterapi i klinikken i Gram. Kreativt terapeutisk redskab til at tilgå komplekse følelser og tilstande ved autoriseret psykolog Marie Aagaard Larsen."
        ogImage="https://assets.auxodev.dk/assets/9bc67d49-c1ad-4360-8231-e9811640bce9/kunstterapi-tegnebord-kreativ.jpg"
      />

      {/* Breadcrumb / Back Link */}
      <div className="flex items-center gap-2 text-xs sm:text-sm text-[#465A4F]">
        <Link to="/" className="hover:text-[#5f7365] underline font-medium">
          Forside
        </Link>
        <span>/</span>
        <span className="text-[#5f7365] font-semibold">Tegne- / kunstterapi</span>
      </div>

      {/* Header */}
      <div className="border-b border-[#E0D7C6] pb-8 space-y-4">
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#5f7365]">
          Tegne- / kunstterapi
        </h1>
        <p className="text-base sm:text-lg text-[#3B4D42] max-w-3xl leading-relaxed">
          Kreativt terapeutisk supplement til at synliggøre, forarbejde og give form til følelser og tilstande, der kan være svære at sætte ord på.
        </p>
        <div className="pt-2 flex flex-wrap items-center gap-4">
          <Link
            to="/kontakt"
            className="px-6 py-3.5 bg-[#5f7365] hover:bg-[#4e6053] text-white font-semibold rounded-none text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2"
          >
            <i className="fa-regular fa-calendar-check" aria-hidden="true" />
            <span>Aftal tid til tegneterapi</span>
          </Link>
          <Link
            to="/priser"
            className="px-5 py-3.5 bg-white hover:bg-[#FAF8F3] text-[#5f7365] border border-[#D5CEBF] font-semibold rounded-none text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2"
          >
            <span>Se priser</span>
            <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-8 space-y-6">
          <div className="space-y-4 text-sm sm:text-base text-[#5f7365] leading-relaxed">
            <h2 className="font-serif text-xl sm:text-2xl font-bold text-[#5f7365]">
              Når ord ikke rækker
            </h2>
            <p>
              Nogle oplevelser, følelser og traumer kan være utroligt vanskelige at udtrykke med rene ord. Tegneterapi og kunstterapi åbner op for en mere direkte og intuitiv tilgang til din indre verden.
            </p>
            <p>
              Gennem tegning, farver og enkle visuelle symboler kan vi gøre "usynlige" psykiske tilstande konkrete og synlige. Det giver et fælles og overskueligt udgangspunkt i terapilokalet.
            </p>

            <h3 className="font-serif text-lg font-bold text-[#5f7365] pt-2">
              Ingen kunstneriske forudsætninger kræves
            </h3>
            <p>
              Du behøver absolut ikke kunne tegne eller have kunstnerisk erfaring for at bruge tegneterapi. Terapien handler ikke om æstetik eller tekniske evner, men om processen og den personlige mening, du lægger i udtrykket.
            </p>
            <p>
              I klinikken har jeg alle nødvendige materialer til rådighed, og vi integrerer altid tegneelementerne nænsomt og efter dit eget ønske i samtaleforløbet.
            </p>

            <div className="pt-2 space-y-2">
              <h4 className="font-serif text-base font-bold text-[#5f7365] flex items-center gap-2">
                <i className="fa-regular fa-lightbulb text-[#5f7365]" aria-hidden="true" />
                Supplerende metode
              </h4>
              <p className="text-xs sm:text-sm text-[#465A4F] leading-relaxed">
                Tegneterapi kombineres ofte med almindelig samtaleterapi, kriseterapi eller Terapikortet, alt efter hvad der giver bedst mening for din proces.
              </p>
            </div>
          </div>
        </div>

        {/* Right Info Box */}
        <div className="lg:col-span-4 bg-[#5f7365] text-white p-6 space-y-5">
          <div className="overflow-hidden border border-[#4e6053]">
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/9bc67d49-c1ad-4360-8231-e9811640bce9/kunstterapi-tegnebord-kreativ.jpg"
              alt="Tegneterapi og kunstterapi i rolige omgivelser"
              className="w-full h-56 object-cover"
            />
          </div>

          <h3 className="font-serif text-lg font-bold text-white">
            Hvem har gavn af tegneterapi?
          </h3>
          <ul className="space-y-3 text-xs sm:text-sm text-[#D5E3DB]">
            <li className="flex items-start gap-2.5">
              <i className="fa-regular fa-check text-[#A3BEAF] font-bold mt-0.5 shrink-0" />
              <span>Børn og unge, som har svært ved at sætte ord på svære følelser.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <i className="fa-regular fa-check text-[#A3BEAF] font-bold mt-0.5 shrink-0" />
              <span>Voksne med komplekse traumer, kriser eller indre tankemylder.</span>
            </li>
            <li className="flex items-start gap-2.5">
              <i className="fa-regular fa-check text-[#A3BEAF] font-bold mt-0.5 shrink-0" />
              <span>Enhver der ønsker en mere visuel og konkret indgang til forandring.</span>
            </li>
          </ul>

          <div className="pt-4 border-t border-[#4e6053] space-y-3">
            <Link
              to="/kontakt"
              className="w-full py-2.5 px-4 bg-[#FAF8F3] hover:bg-white text-[#5f7365] font-semibold rounded-none text-xs flex items-center justify-center gap-2 transition-all duration-200 hover:-translate-y-1"
            >
              <i className="fa-regular fa-envelope" aria-hidden="true" />
              Kontakt Marie for at høre mere
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
